/**
 * Printable fund statement.
 *
 * Rendered as a document, not as an app screen: on paper (or in a PDF) there
 * is no dark mode, no sidebar and no colour-only meaning. The print stylesheet
 * in index.css strips the chrome; everything here is laid out to survive being
 * handed to a neighbour who has never seen the app.
 *
 * "Save as PDF" lives in the browser's own print dialog on every platform,
 * which is why there is no PDF library anywhere in this project.
 */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/misc';
import { Switch } from '@/components/ui/misc';
import { ErrorState } from '@/components/common';
import { Skeleton } from '@/components/ui/misc';
import { formatAmount, formatDate, formatMoney, formatPercent, todayIso } from '@/lib/format';
import { KIND_LABELS } from '@/components/Money';
import type { TransactionKind } from '@/lib/types';

interface StatementCategoryRow {
  categoryId: number | null;
  name: string;
  color: string;
  totalMinor: number;
  share: number;
  count: number;
}

interface StatementEntry {
  id: number;
  date: string;
  kind: TransactionKind;
  description: string | null;
  categoryName: string | null;
  counterpartyName: string | null;
  note: string | null;
  amountMinor: number;
  currency: string;
  decimals: number;
  baseAmountMinor: number;
  counterFundName: string | null;
  counterLocationName: string | null;
}

interface CurrencyLine {
  currency: string;
  decimals: number;
  nativeMinor: number;
  baseMinor: number;
}

interface Statement {
  fund: { id: number; name: string; color: string; note: string | null };
  period: { from: string; to: string };
  baseCurrency: string;
  baseDecimals: number;
  openingMinor: number;
  incomeMinor: number;
  expenseMinor: number;
  transferInMinor: number;
  transferOutMinor: number;
  adjustmentMinor: number;
  closingMinor: number;
  incomeByCategory: StatementCategoryRow[];
  expenseByCategory: StatementCategoryRow[];
  entries: StatementEntry[];
  closingByCurrency: CurrencyLine[];
  closingByLocation: Array<{
    locationName: string;
    holderName: string | null;
    baseMinor: number;
    byCurrency: CurrencyLine[];
  }>;
  closingValuedMinor: number;
  loans: Array<{
    id: number;
    fromFundName: string;
    toFundName: string;
    outstandingMinor: number;
    currency: string;
    decimals: number;
    openedAt: string;
  }>;
  generatedAt: string;
}

function startOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

export function StatementPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const fundId = Number(id);

  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? startOfYear();
  const to = params.get('to') ?? todayIso();
  const [showEntries, setShowEntries] = useState(true);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'statement', fundId, from, to],
    queryFn: async () =>
      (await api.get<Statement>('/reports/statement', { params: { fundId, from, to } })).data,
  });

  const setRange = (key: 'from' | 'to', value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const s = data;
  const money = (m: number) => formatMoney(m, s.baseCurrency, s.baseDecimals);
  const hasForeign = s.closingByCurrency.some((c) => c.currency !== s.baseCurrency);

  return (
    <div>
      {/* ── Controls. Hidden on paper. ── */}
      <div className="no-print mb-6 space-y-4">
        <Button variant="ghost" size="sm" asChild className="-ml-2 gap-1 text-muted-foreground">
          <Link to={`/funds/${fundId}`}>
            <ArrowLeft className="h-4 w-4" />
            Обратно към касата
          </Link>
        </Button>

        <div className="flex flex-wrap items-end gap-3 rounded-lg border p-4">
          <div className="space-y-1.5">
            <Label htmlFor="st-from">От</Label>
            <Input
              id="st-from"
              type="date"
              value={from}
              onChange={(e) => setRange('from', e.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="st-to">До</Label>
            <Input
              id="st-to"
              type="date"
              value={to}
              onChange={(e) => setRange('to', e.target.value)}
              className="w-40"
            />
          </div>

          <div className="flex items-center gap-2 pb-2">
            <Switch id="st-entries" checked={showEntries} onCheckedChange={setShowEntries} />
            <Label htmlFor="st-entries">Опис на операциите</Label>
          </div>

          <Button className="ml-auto gap-2" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            Печат / Запази като PDF
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          В диалога за печат избери <strong>„Запази като PDF"</strong> („Save as PDF" /
          „Destination: Save as PDF"), за да получиш файл, който можеш да изпратиш.
        </p>
      </div>

      {/* ── The document itself ── */}
      <article className="print-document">
        <header className="mb-6 border-b pb-4">
          <h1 className="text-2xl font-bold">Отчет за приходите и разходите</h1>
          <p className="mt-1 text-lg">{s.fund.name}</p>
          {s.fund.note ? <p className="text-sm text-muted-foreground">{s.fund.note}</p> : null}
          <p className="mt-2 text-sm">
            За периода <strong>{formatDate(s.period.from)}</strong> –{' '}
            <strong>{formatDate(s.period.to)}</strong>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Изготвен на {formatDate(s.generatedAt.slice(0, 10))} · всички суми са в{' '}
            {s.baseCurrency}
          </p>
        </header>

        {/* ── Summary. This is the part that must add up. ── */}
        <section className="mb-6 break-inside-avoid">
          <h2 className="mb-2 text-base font-semibold">Обобщение</h2>
          <table className="w-full text-sm">
            <tbody>
              <SummaryRow label={`Салдо към ${formatDate(s.period.from)}`} value={money(s.openingMinor)} />
              <SummaryRow label="Приходи за периода" value={money(s.incomeMinor)} tone="income" />
              <SummaryRow label="Разходи за периода" value={`− ${money(s.expenseMinor)}`} tone="expense" />
              {s.transferInMinor !== 0 ? (
                <SummaryRow label="Получено от други каси" value={money(s.transferInMinor)} />
              ) : null}
              {s.transferOutMinor !== 0 ? (
                <SummaryRow label="Прехвърлено към други каси" value={`− ${money(s.transferOutMinor)}`} />
              ) : null}
              {s.adjustmentMinor !== 0 ? (
                <SummaryRow label="Корекции от ревизия" value={money(s.adjustmentMinor)} />
              ) : null}
              <tr className="border-t-2 font-semibold">
                <td className="py-2">Салдо към {formatDate(s.period.to)}</td>
                <td className="tabular py-2 text-right">{money(s.closingMinor)}</td>
              </tr>
            </tbody>
          </table>

          {hasForeign && s.closingValuedMinor !== s.closingMinor ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Сумите по-горе са по курса към датата на всяка операция, за да се връзва сметката
              точно. Наличността, преоценена по курса към {formatDate(s.period.to)}, е{' '}
              <strong>{money(s.closingValuedMinor)}</strong>. Разликата е от движението на
              валутните курсове, не от движение на пари.
            </p>
          ) : null}
        </section>

        {/* ── Where the money is at the end ── */}
        {s.closingByLocation.length > 0 ? (
          <section className="mb-6 break-inside-avoid">
            <h2 className="mb-2 text-base font-semibold">
              Къде се намират парите към {formatDate(s.period.to)}
            </h2>
            <table className="w-full text-sm">
              <tbody>
                {s.closingByLocation.map((l) => (
                  <tr key={l.locationName} className="border-b last:border-0">
                    <td className="py-1.5">
                      {l.locationName}
                      {l.holderName ? (
                        <span className="text-muted-foreground"> · при {l.holderName}</span>
                      ) : null}
                    </td>
                    <td className="tabular py-1.5 text-right">
                      {l.byCurrency.length > 1 || l.byCurrency[0]?.currency !== s.baseCurrency
                        ? l.byCurrency
                            .map((c) => formatMoney(c.nativeMinor, c.currency, c.decimals))
                            .join(' · ')
                        : money(l.baseMinor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        {/* ── Open debts. Accountability, not decoration. ── */}
        {s.loans.length > 0 ? (
          <section className="mb-6 break-inside-avoid">
            <h2 className="mb-2 text-base font-semibold">Неуредени заеми</h2>
            <table className="w-full text-sm">
              <tbody>
                {s.loans.map((l) => (
                  <tr key={l.id} className="border-b last:border-0">
                    <td className="py-1.5">
                      {l.toFundName} дължи на {l.fromFundName}
                      <span className="text-muted-foreground"> · от {formatDate(l.openedAt)}</span>
                    </td>
                    <td className="tabular py-1.5 text-right">
                      {formatMoney(l.outstandingMinor, l.currency, l.decimals)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <div className="mb-6 grid gap-6 sm:grid-cols-2">
          <CategoryTable
            title="Приходи по пера"
            rows={s.incomeByCategory}
            total={s.incomeMinor}
            money={money}
          />
          <CategoryTable
            title="Разходи по пера"
            rows={s.expenseByCategory}
            total={s.expenseMinor}
            money={money}
          />
        </div>

        {/* ── Itemised list ── */}
        {showEntries ? (
          <section>
            <h2 className="mb-2 text-base font-semibold">
              Опис на операциите ({s.entries.length})
            </h2>
            {s.entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">Няма операции в този период.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs">
                    <th className="py-1.5 font-medium">Дата</th>
                    <th className="py-1.5 font-medium">Описание</th>
                    <th className="py-1.5 font-medium">Перо</th>
                    <th className="py-1.5 text-right font-medium">Сума</th>
                  </tr>
                </thead>
                <tbody>
                  {s.entries.map((e) => (
                    <tr key={e.id} className="break-inside-avoid border-b last:border-0">
                      <td className="tabular whitespace-nowrap py-1.5 align-top">
                        {formatDate(e.date)}
                      </td>
                      <td className="py-1.5 align-top">
                        {e.description || KIND_LABELS[e.kind]}
                        {e.counterpartyName ? (
                          <span className="text-muted-foreground"> · {e.counterpartyName}</span>
                        ) : null}
                        {e.counterFundName ? (
                          <span className="text-muted-foreground">
                            {' '}
                            · {e.amountMinor < 0 ? 'към' : 'от'} {e.counterFundName}
                          </span>
                        ) : null}
                        {e.note ? (
                          <div className="text-xs text-muted-foreground">{e.note}</div>
                        ) : null}
                      </td>
                      <td className="py-1.5 align-top text-muted-foreground">
                        {e.categoryName ?? '—'}
                      </td>
                      <td
                        className={cn(
                          'tabular whitespace-nowrap py-1.5 text-right align-top',
                          e.amountMinor > 0 ? 'text-income' : 'text-expense',
                        )}
                      >
                        {e.amountMinor > 0 ? '+' : '−'}
                        {formatAmount(Math.abs(e.amountMinor), e.decimals)}{' '}
                        {e.currency !== s.baseCurrency ? e.currency : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ) : null}

        <footer className="mt-8 border-t pt-4 text-xs text-muted-foreground">
          <p>
            Отчетът е генериран автоматично от записите в money-keeper. Всяка поправка в
            системата се прави със сторно — операциите не се изтриват, така че описът съответства
            на пълната история.
          </p>
          <div className="mt-8 flex gap-16">
            <div>
              <div className="w-48 border-b border-foreground/40" />
              <p className="mt-1">Изготвил</p>
            </div>
            <div>
              <div className="w-48 border-b border-foreground/40" />
              <p className="mt-1">Приел</p>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'income' | 'expense';
}): JSX.Element {
  return (
    <tr className="border-b">
      <td className="py-1.5">{label}</td>
      <td
        className={cn(
          'tabular py-1.5 text-right',
          tone === 'income' && 'text-income',
          tone === 'expense' && 'text-expense',
        )}
      >
        {value}
      </td>
    </tr>
  );
}

function CategoryTable({
  title,
  rows,
  total,
  money,
}: {
  title: string;
  rows: StatementCategoryRow[];
  total: number;
  money: (m: number) => string;
}): JSX.Element {
  return (
    <section className="break-inside-avoid">
      <h2 className="mb-2 text-base font-semibold">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Няма движения.</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.categoryId ?? 'none'}-${i}`} className="border-b">
                <td className="py-1.5">
                  {r.name}
                  <span className="text-xs text-muted-foreground"> ({r.count})</span>
                </td>
                <td className="tabular py-1.5 text-right">
                  {money(r.totalMinor)}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {formatPercent(r.share)}
                  </span>
                </td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="py-1.5">Общо</td>
              <td className="tabular py-1.5 text-right">{money(total)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
}
